import React, { useState } from "react";
import {
    Badge,
    Button,
    Modal,
    List,
    Row,
    Col,
    Checkbox,
    Tag,
    Tooltip,
} from "antd";
import { LuListFilter } from "react-icons/lu";
import {
    filter,
    some,
    values,
    reduce,
    map,
    pairs,
    flatten,
    size,
    contains,
} from "underscore";
import {
    IMainFilterState,
    IFilterData,
    TOptionsString,
    IFilterTag,
} from "@/ts/common-types";
import { capitalizeFirst, titleCase } from "@/ts/utils";
import { FilterInternals } from "./FilterInternals";
import { useDispatch } from "react-redux";
import { applyAdditionalFilters, closeAdditionalFiltersModal, openAdditionalFiltersModal, resetAdditionalFiltersState, resetRevstackDashboardWidgetData, setAdditionalFiltersActiveIndex, setAdditionalFiltersSearchText, updateAdditionalFiltersDraft } from "@/features/revstackWidgetsSlice";
import { applyCurrentFiltersWithHash } from "@/ts/revstack/filters";

interface IRevstackFiltersMenuProps {
    filterData: IFilterData[];
    disabled?: boolean;
    state: IMainFilterState;
}

enum MenuActions {
    RESET = "reset",
    APPLY = "apply",
}

const MODAL_STYLES = {
    header: { padding: "10px 16px" },
    body: { padding: 0 },
    footer: { padding: 16 },
    content: {
        borderRadius: 16,
        position: "absolute",
        top: 50,
        right: -100,
        left: 180,
    },
    mask: { position: "absolute", top: 145, right: 0 },
} as const;

const RevstackFiltersMenu: React.FC<IRevstackFiltersMenuProps> = ({
    filterData,
    disabled = false,
    state,
}) => {
    const dispatch = useDispatch();
    const {
        isOpen = false,
        appliedFilters = {},
        draftFilters = {},
        activeIndex = 0,
        searchText = ""
    } = state || {};

    const [showMoreTags, setShowMoreTags] = useState(false);
    const MAX_TAGS = 4;

    const currentFilter = filterData[activeIndex];
    const allOptions = currentFilter?.options || [];
    const filteredOptions = filter(allOptions, (opt) =>
        opt.label.toLowerCase().includes(searchText.toLowerCase())
    );

    const hasFilters = some(values(appliedFilters), (collection) => size(collection) > 0);
    const totalDraftCount = reduce(
        values(draftFilters),
        (total, collection) => total + size(collection),
        0
    );
    const totalSelectedCount = reduce(
        values(appliedFilters),
        (total, collection) => total + size(collection),
        0
    );
    const totalOptionsCount = reduce(
        filterData,
        (total, filter) => total + filter.options.length,
        0
    );

    const handleToggleModal = (): void => {
        if (isOpen) {
            dispatch(closeAdditionalFiltersModal());
        } else {
            dispatch(openAdditionalFiltersModal());
        }
    };

    const handleUpdateDraftFilter = (option: TOptionsString, checked: boolean): void => {
        dispatch(updateAdditionalFiltersDraft({
            tagTypeId: currentFilter.value,
            option: Number(option.value),
            checked,
        }));
    };

    const handleResetSearch = (): void => {
        dispatch(setAdditionalFiltersSearchText(""));
    };

    const renderAppliedFilterTags = (): JSX.Element => {
        const allTags: IFilterTag[] = flatten(
            map(pairs(draftFilters), ([tagTypeId, collection]) => {
                const collectionArray: number[] = Array.isArray(collection) ? collection : Array.from(collection as Set<string> || []).map(Number);
                return map(collectionArray, (value: number): IFilterTag => {
                    let displayLabel: string = String(value);
                    const numTagTypeId = Number(tagTypeId);
                    const filter = filterData.find(f => f.value === numTagTypeId);
                    if (filter) {
                        const option = filter.options.find(
                            (opt: TOptionsString) => Number(opt.value) === value
                        );
                        if (option) {
                            displayLabel = option.label;
                        }
                    }
                    return {
                        filterLabel: tagTypeId,
                        value: String(value),
                        displayLabel,
                    };
                });
            })
        );

        const displayedTags: IFilterTag[] = showMoreTags
            ? allTags
            : allTags.slice(0, MAX_TAGS);
        const remaining: number = allTags.length - MAX_TAGS;

        return (
            <div className="filter-pills-section">
                {displayedTags.map(({ filterLabel, value, displayLabel }) => (
                    <Tag
                        className="filter-tags"
                        key={`${filterLabel}-${value}`}
                        closable
                        onClose={() => dispatch(updateAdditionalFiltersDraft({ tagTypeId: Number(filterLabel), option: Number(value), checked: false }))}
                    >
                        {displayLabel}
                    </Tag>
                ))}
                {remaining > 0 && (
                    <span
                        className="filter-tags-more-btn"
                        onClick={() =>
                            setShowMoreTags((prev: boolean) => !prev)
                        }
                    >
                        {showMoreTags ? "− Show Less" : "+ Show More"}
                    </span>
                )}
            </div>
        );
    };

    const renderFilterListItem = (filter: IFilterData, idx: number): JSX.Element => {
        const isActive: boolean = idx === activeIndex;
        const hasApplied: boolean = size(draftFilters[filter.value]) > 0;
        const itemClassName: string = `advanced-filters-panel-item px-3 py-2 ${
            isActive ? "active" : ""
        }`;

        return (
            <List.Item
                key={filter.value}
                onClick={() => dispatch(setAdditionalFiltersActiveIndex(idx))}
                tabIndex={0}
                role="button"
                className={itemClassName}
            >
                <span>{filter.label}</span>
                {hasApplied && (
                    <div className="advanced-filters-panel-active-circle ms-2 rounded-circle" />
                )}
            </List.Item>
        );
    };

    const isOptionSelected = (option: TOptionsString): boolean => {
        const collection = draftFilters[currentFilter?.value];
        if (!collection) return false;
        return contains(collection, option.value);
    };

    const truncateLabel = (label: string, maxLength: number = 26): string => {
        if (label.length <= maxLength) return label;
        return label.substring(0, maxLength) + "...";
    };

    const renderFilterOptions = (): JSX.Element[] => {
        return map(filteredOptions, (option: TOptionsString, index: number) => {
            const isTruncated: boolean = option.label.length > 26;
            const displayLabel: string = truncateLabel(capitalizeFirst(option.label));

            return (
                <Checkbox
                    key={`${option.value}-${index}`}
                    checked={isOptionSelected(option)}
                    onChange={(e) => handleUpdateDraftFilter(option, e.target.checked)}
                >
                    {isTruncated ? (
                        <Tooltip title={option.label}>
                            <span>{displayLabel}</span>
                        </Tooltip>
                    ) : (
                        displayLabel
                    )}
                </Checkbox>
            );
        });
    };

    const menuActionsHandler = (action: MenuActions) => {
        if (action === MenuActions.RESET) {
            dispatch(resetAdditionalFiltersState());
        } else {
            dispatch(resetRevstackDashboardWidgetData());
            dispatch(applyAdditionalFilters());
        }
        applyCurrentFiltersWithHash();
    };

    return (
        <>
            <span
                style={{ display: "inline-flex", alignItems: "center" }}
                onClick={handleToggleModal}
            >
                <Badge count={hasFilters ? totalSelectedCount : 0} color="red">
                    <Button
                        disabled={disabled}
                        size="middle"
                        className={`revstack-filter-button d-flex align-items-center justify-content-center ${
                            isOpen || hasFilters ? "active" : ""
                        }`}
                    >
                        <LuListFilter style={{ fontSize: 16 }} />
                        <span className="filter-button-text">Filters</span>
                    </Button>
                </Badge>
            </span>

            <Modal
                open={isOpen}
                onCancel={() => dispatch(closeAdditionalFiltersModal())}
                title={
                    <span className="advanced-filters-modal-title">
                        Additional Filters
                    </span>
                }
                width={844}
                wrapClassName="revstack-filter-modal"
                footer={
                    <div className="d-flex justify-content-end align-items-center">
                        <Button
                            type="link"
                            className="advanced-filters-clear-all me-3 p-0"
                            disabled={totalSelectedCount === 0}
                            onClick={() => menuActionsHandler(MenuActions.RESET)}
                        >
                            Clear All
                        </Button>
                        <Button
                            className="revstack-btn-primary"
                            disabled={totalDraftCount === 0}
                            onClick={() => menuActionsHandler(MenuActions.APPLY)}
                        >
                            Apply
                        </Button>
                    </div>
                }
                styles={MODAL_STYLES}
            >
                <div className="filter-dropdown-head py-3 px-4">
                        <div className="filter-dropdown-selection-labels pb-3">
                            <span className="selected">
                                {totalDraftCount} Selected
                            </span>
                            <span className="total">
                                Total {totalOptionsCount}
                            </span>
                        </div>
                        <div className="filter-pills-section">
                            {renderAppliedFilterTags()}
                        </div>
                    </div>
                <Row>
                    <Col
                        span={7}
                        className="advanced-filters-options-cols border-end"
                    >
                        <List
                            dataSource={filterData}
                            renderItem={renderFilterListItem}
                            className="h-100 overflow-auto"
                        />
                    </Col>

                    <Col span={17} className="advanced-filters-options-cols">
                        <FilterInternals
                            selectedCount={totalSelectedCount}
                            totalCount={totalOptionsCount}
                            selectedTags={renderAppliedFilterTags()}
                            searchPlaceholder={`Search for ${titleCase(
                                currentFilter?.label || ""
                            )}`}
                            searchValue={searchText}
                            onSearchChange={(text: string) => dispatch(setAdditionalFiltersSearchText(text))}
                            onSearchReset={handleResetSearch}
                            mainContent={
                                filteredOptions.length > 0 ? (
                                    <div className="advanced-filters-options-container">
                                        {renderFilterOptions()}
                                    </div>
                                ) : (
                                    <div
                                        style={{
                                            padding: "20px",
                                            color: "#666",
                                            fontStyle: "italic",
                                        }}
                                    >
                                        No matching filter options.
                                    </div>
                                )
                            }
                            searchInputType="input"
                            containerPadding="px-4 pt-4"
                            mainContentMaxHeight="100%"
                            useSimpleLayout={true}
                        />
                    </Col>
                </Row>
            </Modal>
        </>
    );
};

export default RevstackFiltersMenu;